<script>
export default {
  name: "LuxuryPrizeWheel",
  props: {
    prizes: {
      type: Array,
      required: true,
      validator: value => value.length > 1
    },
    remainingSpins: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      rotation: 0,
      isSpinning: false,
      selectedPrize: null
    };
  },
  computed: {
    segmentAngle() {
      return 360 / this.prizes.length;
    },
    wheelStyle() {
      const colorStops = this.prizes
        .map((prize, index) => {
          const start = index * this.segmentAngle;
          const end = start + this.segmentAngle;
          return `${prize.color} ${start}deg ${end}deg`;
        })
        .join(", ");

      return {
        "--wheel-rotation": `${this.rotation}deg`,
        "--segment-offset": `${this.segmentAngle / 2}deg`,
        background: `conic-gradient(from -${this.segmentAngle /
          2}deg, ${colorStops})`
      };
    }
  },
  methods: {
    prizeStyle(index, prize) {
      const angle = index * this.segmentAngle;
      return {
        color: prize.textColor || "#fff9ed",
        transform: `rotate(${angle}deg) translateY(calc(var(--label-radius) * -1)) rotate(${-angle}deg)`
      };
    },
    lightStyle(index) {
      return {
        transform: `rotate(${index *
          18}deg) translateY(calc(var(--light-radius) * -1))`
      };
    },
    spin() {
      if (this.isSpinning || this.remainingSpins < 1) return;

      const winnerIndex = Math.floor(Math.random() * this.prizes.length);
      const normalizedRotation = ((this.rotation % 360) + 360) % 360;
      const winningRotation = (360 - winnerIndex * this.segmentAngle) % 360;
      const alignmentDelta = (winningRotation - normalizedRotation + 360) % 360;

      this.selectedPrize = this.prizes[winnerIndex];
      this.isSpinning = true;
      this.rotation += 360 * 6 + alignmentDelta;
      this.$emit("spin-start");

      if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(35);
      }
    },
    handleTransitionEnd(event) {
      if (!this.isSpinning || event.propertyName !== "transform") return;

      this.isSpinning = false;
      this.$emit("spin-complete", this.selectedPrize);
    }
  }
};
</script>

<template>
  <section class="wheel-stage" aria-label="วงล้อรางวัล">
    <div class="wheel-stage__halo wheel-stage__halo--outer"></div>
    <div class="wheel-stage__halo wheel-stage__halo--inner"></div>

    <div class="wheel-pointer" aria-hidden="true">
      <span class="wheel-pointer__gem"></span>
    </div>

    <div class="wheel-shell">
      <span
        v-for="lightIndex in 20"
        :key="`light-${lightIndex}`"
        class="wheel-light"
        :class="{ 'wheel-light--bright': lightIndex % 2 === 0 }"
        :style="lightStyle(lightIndex - 1)"
      ></span>

      <div class="wheel-bezel">
        <div
          class="prize-wheel"
          :class="{ 'prize-wheel--spinning': isSpinning }"
          :style="wheelStyle"
          @transitionend="handleTransitionEnd"
        >
          <span
            v-for="dividerIndex in prizes.length"
            :key="`divider-${dividerIndex}`"
            class="prize-wheel__divider"
            :style="{
              transform: `rotate(${(dividerIndex - 1) * segmentAngle -
                segmentAngle / 2}deg)`
            }"
          ></span>

          <span
            v-for="(prize, index) in prizes"
            :key="prize.id"
            class="prize-wheel__label"
            :class="{ 'prize-wheel__label--jackpot': prize.featured }"
            :style="prizeStyle(index, prize)"
          >
            <small v-if="prize.featured">JACKPOT</small>
            <strong>{{ prize.amount }}</strong>
            <em>{{ prize.unit }}</em>
          </span>
        </div>

        <button
          class="spin-control"
          type="button"
          :disabled="isSpinning || remainingSpins < 1"
          :aria-label="isSpinning ? 'กำลังหมุนวงล้อ' : 'หมุนวงล้อ'"
          @click="spin"
        >
          <span class="spin-control__shine"></span>
          <span class="spin-control__crown" aria-hidden="true">♛</span>
          <strong>{{ isSpinning ? "รอลุ้น" : "SPIN" }}</strong>
          <small>{{ isSpinning ? "GOOD LUCK" : "กดเพื่อหมุน" }}</small>
        </button>
      </div>
    </div>

    <p class="wheel-stage__hint">
      <span aria-hidden="true"></span>
      {{
        isSpinning
          ? "วงล้อกำลังเลือกรางวัลของคุณ"
          : "แตะปุ่มกลางวงล้อเพื่อเริ่มลุ้น"
      }}
      <span aria-hidden="true"></span>
    </p>
  </section>
</template>

<style lang="scss" scoped>
.wheel-stage {
  --wheel-size: min(82vw, 390px, 53vh);
  --label-radius: calc(var(--wheel-size) * 0.33);
  --light-radius: calc(var(--wheel-size) * 0.478);
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  isolation: isolate;
}

.wheel-stage__halo {
  position: absolute;
  left: 50%;
  top: calc(var(--wheel-size) / 2);
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.wheel-stage__halo--outer {
  width: calc(var(--wheel-size) * 1.34);
  height: calc(var(--wheel-size) * 1.34);
  background: radial-gradient(
    circle,
    rgba(255, 201, 91, 0.28),
    rgba(177, 0, 50, 0.12) 45%,
    transparent 70%
  );
  filter: blur(15px);
  animation: haloBreath 3.2s ease-in-out infinite;
}

.wheel-stage__halo--inner {
  width: calc(var(--wheel-size) * 1.08);
  height: calc(var(--wheel-size) * 1.08);
  border: 1px solid rgba(255, 229, 164, 0.22);
  box-shadow: 0 0 45px rgba(243, 191, 91, 0.18);
}

.wheel-pointer {
  position: absolute;
  top: calc(var(--wheel-size) * -0.015);
  left: 50%;
  z-index: 10;
  width: calc(var(--wheel-size) * 0.14);
  height: calc(var(--wheel-size) * 0.2);
  transform: translateX(-50%);
  clip-path: polygon(50% 100%, 2% 14%, 24% 0, 76% 0, 98% 14%);
  background: linear-gradient(90deg, #16171a 0%, #4a4b50 48%, #111216 100%);
  filter: drop-shadow(0 8px 7px rgba(0, 0, 0, 0.52));
}

.wheel-pointer::after {
  content: "";
  position: absolute;
  inset: 5px;
  clip-path: inherit;
  background: linear-gradient(145deg, #55565a, #25262a 48%, #0d0e10 80%);
}

.wheel-pointer__gem {
  position: absolute;
  left: 50%;
  top: 14%;
  z-index: 1;
  width: 28%;
  aspect-ratio: 1;
  border: 1px solid rgba(255, 255, 255, 0.95);
  transform: translateX(-50%) rotate(45deg);
  background: linear-gradient(145deg, #ffffff, #eee8dc 48%, #9fa0a4);
  box-shadow: 0 0 12px rgba(255, 239, 196, 0.9);
}

.wheel-shell {
  position: relative;
  width: var(--wheel-size);
  height: var(--wheel-size);
  border-radius: 50%;
  background: radial-gradient(
      circle at 35% 30%,
      rgba(255, 255, 255, 0.94) 0 1%,
      transparent 2%
    ),
    linear-gradient(
      145deg,
      #fff1b7 0%,
      #b36a17 22%,
      #f6d777 48%,
      #72400d 72%,
      #ffeaa5 100%
    );
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.56),
    0 0 0 2px rgba(255, 231, 173, 0.42), 0 0 32px rgba(221, 160, 57, 0.28),
    inset 0 0 0 3px rgba(78, 39, 6, 0.7);
}

.wheel-shell::before,
.wheel-shell::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
}

.wheel-shell::before {
  inset: 3%;
  z-index: 2;
  border: 1px solid rgba(255, 246, 206, 0.48);
  box-shadow: inset 0 0 17px rgba(255, 245, 198, 0.42);
}

.wheel-shell::after {
  inset: 0;
  z-index: 5;
  background: linear-gradient(
    128deg,
    rgba(255, 255, 255, 0.2),
    transparent 24% 69%,
    rgba(73, 31, 0, 0.18)
  );
}

.wheel-light {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 7;
  width: calc(var(--wheel-size) * 0.022);
  height: calc(var(--wheel-size) * 0.022);
  margin: calc(var(--wheel-size) * -0.011);
  border-radius: 50%;
  background: #fff2b0;
  box-shadow: 0 0 8px #ffc95d, 0 0 15px rgba(255, 214, 106, 0.72);
}

.wheel-light--bright {
  background: #ffffff;
  box-shadow: 0 0 9px #fff, 0 0 17px #ffd066, 0 0 25px rgba(255, 185, 45, 0.7);
  animation: jewelBlink 1.6s ease-in-out infinite alternate;
}

.wheel-bezel {
  position: absolute;
  inset: 6.4%;
  overflow: hidden;
  border: calc(var(--wheel-size) * 0.012) solid rgba(104, 56, 12, 0.9);
  border-radius: 50%;
  background: #26272b;
  box-shadow: 0 0 0 2px rgba(255, 224, 140, 0.64),
    inset 0 0 24px rgba(0, 0, 0, 0.8);
}

.prize-wheel {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  transform: rotate(var(--wheel-rotation));
  transition: transform 4.9s cubic-bezier(0.12, 0.62, 0.08, 1);
  will-change: transform;
}

.prize-wheel::before,
.prize-wheel::after {
  content: "";
  position: absolute;
  z-index: 1;
  border-radius: 50%;
  pointer-events: none;
}

.prize-wheel::before {
  inset: 0;
  background: radial-gradient(
      circle at 38% 26%,
      rgba(255, 255, 255, 0.28),
      transparent 11%
    ),
    radial-gradient(
      circle,
      transparent 46%,
      rgba(45, 45, 49, 0.13) 67%,
      rgba(0, 0, 0, 0.45) 100%
    );
}

.prize-wheel::after {
  inset: 1px;
  border: 1px solid rgba(255, 237, 183, 0.38);
  box-shadow: inset 0 0 18px rgba(255, 221, 133, 0.24);
}

.prize-wheel__divider {
  position: absolute;
  left: 50%;
  top: 0;
  z-index: 2;
  width: 1px;
  height: 50%;
  transform-origin: 50% 100%;
  background: linear-gradient(
    180deg,
    rgba(111, 75, 19, 0.82),
    rgba(126, 81, 17, 0.12)
  );
  box-shadow: 0 0 2px rgba(255, 222, 139, 0.34);
}

.prize-wheel__label {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 3;
  display: flex;
  width: calc(var(--wheel-size) * 0.19);
  height: calc(var(--wheel-size) * 0.14);
  margin: calc(var(--wheel-size) * -0.07) 0 0 calc(var(--wheel-size) * -0.095);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: "Tahoma", sans-serif;
  line-height: 1;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.34);
}

.prize-wheel__label strong {
  font-family: Georgia, "Times New Roman", serif;
  font-size: calc(var(--wheel-size) * 0.055);
  font-weight: 700;
  letter-spacing: -0.04em;
}

.prize-wheel__label em {
  margin-top: 3px;
  color: currentColor;
  font-size: calc(var(--wheel-size) * 0.027);
  font-style: normal;
  font-weight: 600;
  opacity: 0.82;
}

.prize-wheel__label small {
  margin-bottom: 3px;
  color: #ffe49b;
  font-family: Georgia, "Times New Roman", serif;
  font-size: calc(var(--wheel-size) * 0.019);
  font-weight: 700;
  letter-spacing: 0.12em;
}

.prize-wheel__label--jackpot strong {
  color: #fff0a8;
  text-shadow: 0 0 10px rgba(255, 221, 119, 0.82), 0 2px 4px rgba(0, 0, 0, 0.6);
}

.spin-control {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 8;
  display: flex;
  width: calc(var(--wheel-size) * 0.31);
  aspect-ratio: 1;
  overflow: hidden;
  border: calc(var(--wheel-size) * 0.012) solid #e9b84c;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transform: translate(-50%, -50%);
  background: radial-gradient(
      circle at 36% 28%,
      rgba(255, 255, 255, 0.8) 0 3%,
      transparent 10%
    ),
    radial-gradient(
      circle at 50% 48%,
      #ffe99c 0%,
      #e9aa35 42%,
      #a96313 70%,
      #5a2c08 100%
    );
  color: #4f2608;
  box-shadow: 0 7px 16px rgba(0, 0, 0, 0.5), 0 0 0 3px #713b0c,
    0 0 0 5px rgba(255, 222, 125, 0.82),
    inset 0 3px 7px rgba(255, 255, 255, 0.72),
    inset 0 -6px 10px rgba(90, 43, 3, 0.32);
  transition: transform 180ms ease, filter 180ms ease;
}

.spin-control:active:not(:disabled) {
  transform: translate(-50%, -50%) scale(0.94);
}

.spin-control:focus-visible {
  outline: 3px solid #fff7cb;
  outline-offset: 6px;
}

.spin-control:disabled {
  cursor: wait;
}

.spin-control__shine {
  position: absolute;
  top: -30%;
  left: -55%;
  width: 38%;
  height: 160%;
  transform: rotate(25deg);
  background: rgba(255, 255, 255, 0.45);
  filter: blur(3px);
  animation: buttonShine 3.8s ease-in-out infinite;
}

.spin-control__crown {
  margin-bottom: -2px;
  font-size: calc(var(--wheel-size) * 0.046);
}

.spin-control strong {
  font-family: Georgia, "Times New Roman", serif;
  font-size: calc(var(--wheel-size) * 0.07);
  letter-spacing: 0.05em;
}

.spin-control small {
  margin-top: 3px;
  font-size: calc(var(--wheel-size) * 0.022);
  font-weight: 700;
}

.wheel-stage__hint {
  display: flex;
  margin-top: 20px;
  align-items: center;
  gap: 12px;
  color: rgba(255, 244, 219, 0.78);
  font-size: clamp(11px, 3vw, 13px);
  font-weight: 500;
  letter-spacing: 0.02em;
}

.wheel-stage__hint span {
  width: 20px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(229, 190, 99, 0.7));
}

.wheel-stage__hint span:last-child {
  transform: rotate(180deg);
}

@keyframes haloBreath {
  0%,
  100% {
    opacity: 0.55;
    transform: translate(-50%, -50%) scale(0.96);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.04);
  }
}

@keyframes jewelBlink {
  from {
    opacity: 0.62;
    filter: brightness(0.76);
  }
  to {
    opacity: 1;
    filter: brightness(1.3);
  }
}

@keyframes buttonShine {
  0%,
  62% {
    left: -55%;
  }
  80%,
  100% {
    left: 125%;
  }
}

@media (max-height: 690px) {
  .wheel-stage {
    --wheel-size: min(74vw, 335px, 48vh);
  }

  .wheel-stage__hint {
    margin-top: 12px;
  }
}

@media (orientation: landscape) and (max-height: 560px) {
  .wheel-stage {
    --wheel-size: min(54vh, 320px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .wheel-stage__halo--outer,
  .wheel-light--bright,
  .spin-control__shine {
    animation: none;
  }

  .prize-wheel {
    transition-duration: 1.4s;
  }
}
</style>
